import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler,
  ChartOptions,
} from 'chart.js';
import { useEffect, useMemo, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import { ResponsiveContainer } from 'recharts'; // still usable as a wrapper

import { chartTimePeriod } from '../../../config/enum';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { setNotificationDetails } from '../../../redux/slice/NotificationSlice';
import type { RootState } from '../../../redux/store';
import BatteryLoader from '../../../shared-components/battery-loader/BatteryLoader';
import CustomToggleButton from '../../../shared-components/custom-toggle-button/CustomToggleButton';
import ToggleWithIcons from '../../../shared-components/toggle-with-icons/ToggleWithIcons';
import {
  fillMissingLast24HrsGeneric,
  fillMissingLastNDaysGeneric,
  fillMissingMonthsGeneric,
} from '../../../shared-functions/ChartFunctions';
import stationManagement from '../../../utils/api/station-management';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend, Filler);

interface ChartDataProperties {
  label: string; // month/day/hour
  totalRevenue: number;
  totalAmountPaid: number;
  totalPayableAmount: number;
  monthName: string;
}

/**
 * Energy Revenue Overlay Chart
 * @returns {any} The Energy Revenue Overlay Chart component.
 */
function EnergyRevenueOverlayChart() {
  const { t } = useTranslation('stationManagement');
  const dispatch = useAppDispatch();
  const { selectedStationIds } = useAppSelector((state) => state.stationComparison);
  const chargingStationList = useAppSelector(
    (state: RootState) => state.stationManagement.chargingStationList,
  );
  const [chartToggle, setChartToggle] = useState([
    {
      label: 'energy',
      key: 'powerConsumption',
      isActive: true,
    },
    {
      label: 'revenueOverlay',
      key: 'revenue',
      isActive: false,
    },
  ]);
  const [activeIndexToggle, setActiveIndexToggle] = useState(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [graphData, setGraphData] = useState<ChartDataProperties[]>([]);
  //    const [graphData, setGraphData] = useState<(Partial<ChartDataProperties> & { label: string })[]>(
  //     [],
  //   );
  const [chartData, setChartData] = useState<any>(null);
  const chartOptions: ChartOptions<'line'> = {
    responsive: true,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          /**
           * hides all legend items (no “radio buttons”)
           * @returns \
           */
          filter: () => false,
        },
      },
      tooltip: {
        backgroundColor: '#1F2937',
        titleColor: '#FFFFFF',
        bodyColor: '#FFFFFF',
        padding: 10,
        cornerRadius: 8,
        displayColors: true,
      },
    },
    interaction: {
      intersect: false,
    },
    scales: {
      x: {
        grid: { display: false },
        border: { display: false },
      },
      y: {
        grid: { display: false }, //  remove Y grid lines
        border: { display: false }, //  remove Y axis line
        ticks: {
          display: true, // SHOW ticks
          count: 5,
          /**
           * Showing value with kW
           * @param {number | string} value The data value.
           * @returns {string} The formatted value with unit.
           */
          callback: (value) => `${value} ${t('kw')}`, // FORMAT ticks
          color: '#6b7280', // optional: nice gray
          font: {
            size: 11,
            weight: 500,
          },
        },
      },
    },
  };
  const stationIdsToUse = useMemo(() => {
    const selected = selectedStationIds.filter(Boolean);

    // If user selected stations → use them
    if (selected.length > 0) {
      return selected;
    }

    // Otherwise → use all charging stations
    return chargingStationList.map((station) => station._id);
  }, [selectedStationIds, chargingStationList]);

  /**
   * Fetches the revenue vs payable report data for the specified time range and updates the chart.
   * @param range - The time period for the report ('last24hours', 'last7days', 'lastMonth', or 'last12months').
   */
  const fetchReport = async (
    range: 'last24hours' | 'last7days' | 'lastMonth' | 'last12months',
    type: string,
  ) => {
    if (chargingStationList.length === 0) return;

    setLoading(true);
    try {
      const response = await stationManagement.fetchEnergyRevenueComparison({
        chargingStations: stationIdsToUse,
        timePeriod: range,
        type,
      });
      if (response.status === 200) {
        const { data } = response;

        let filledData: any[] = [];
        switch (range) {
          case 'last12months':
            filledData = fillMissingMonthsGeneric(data);
            break;
          case 'lastMonth':
            filledData = fillMissingLastNDaysGeneric(data, 30);
            break;
          case 'last7days':
            filledData = fillMissingLastNDaysGeneric(data, 7);
            break;
          case 'last24hours':
            filledData = fillMissingLast24HrsGeneric(data);
            break;
          default:
            filledData = data;
        }

        setGraphData(filledData);
      }
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      dispatch(
        setNotificationDetails({
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || t('somethingWentWrong'),
          },
        }),
      );
    }
  };

  /**
   * Function to switch the tab
   * @param {string} key The toggle key.
   */
  const handleClickToggle = (key: string) => {
    setChartToggle((previousTabs) =>
      previousTabs.map((tab) => {
        if (tab.key === key) {
          return {
            ...tab,
            isActive: true,
          };
        }
        return {
          ...tab,
          isActive: false,
        };
      }),
    );

    fetchReport(chartTimePeriod[activeIndexToggle]?.value || 'last24hours', key || 'userCount');
  };

  useEffect(() => {
    if (chargingStationList.length === 0) return;

    fetchReport(
      chartData || 'last24hours',
      chartToggle.find((toggle) => toggle.isActive)?.key || 'userCount',
    );
  }, [stationIdsToUse]);

  const labels = graphData.map((d) => d.label || d.monthName);

  // Extract all unique station IDs dynamically
  const allStationIds = Array.from(
    new Set(graphData.flatMap((d: any) => Object.keys(d.stationData || {}))),
  );

  const colorPalette = ['#00b894', '#6c5ce7', '#d63031', '#0984e3'];

  const datasets = allStationIds.map((stationId, index) => ({
    label: `${t('station')} ${stationId.slice(0, 6)}`,
    data: graphData.map((d: any) => d.stationData?.[stationId] ?? 0),
    borderColor: colorPalette[index % colorPalette.length],
    backgroundColor: `${colorPalette[index % colorPalette.length]}33`,
    fill: false,
    tension: 0.4,
    borderWidth: 1.5,
    pointRadius: 0,
    pointHoverRadius: 3,
  }));

  return (
    <div className="energy-revenue-chart-wrap">
      <div className="energy-revenue-chart-container">
        <div className="energy-revenue-header">
          <div className="energy-revenue-title">
            <ToggleWithIcons
              leftMenu={{ ...chartToggle[0], label: t(chartToggle[0].label) }}
              rightMenu={{ ...chartToggle[1], label: t(chartToggle[1].label) }}
              handleClickToggle={handleClickToggle}
            />
          </div>
          <CustomToggleButton
            labels={chartTimePeriod.map((duration) => t(duration.label))}
            onToggle={(index) => {
              const selected = chartTimePeriod[index];
              if (selected) {
                fetchReport(
                  selected.value,
                  chartToggle.find((toggle) => toggle.isActive)?.key || 'userCount',
                );
                setChartData(selected.value);
              }
              setActiveIndexToggle(index);
            }}
            defaultActiveIndex={activeIndexToggle}
            type="chart"
          />
        </div>
        <div className="energy-revenue-chart-container">
          <div className="energy-revenue-chart">
            {loading ? (
              <div className="loader">
                <BatteryLoader />
              </div>
            ) : (
              <ResponsiveContainer height={230}>
                <Line
                  data={{
                    labels,
                    datasets,
                  }}
                  options={chartOptions}
                />
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default EnergyRevenueOverlayChart;
